import type * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Tabs as TabsPrimitive } from "radix-ui";

import { cn } from "~/lib/utils";

function Tabs({
  className,
  orientation = "horizontal",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      className={cn("group/tabs flex data-horizontal:flex-col", className)}
      {...props}
    />
  );
}

const tabsListVariants = cva(
  "flex w-full items-end p-0 rounded-none group-data-horizontal/tabs:h-8 data-[variant=default]:border-b data-[variant=default]:border-border data-[variant=line]:rounded-none group/tabs-list text-muted-foreground justify-center group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        line: "gap-1 bg-transparent",
      },
      layout: {
        default: "",
        compact: "justify-start",
      },
    },
    defaultVariants: {
      variant: "default",
      layout: "default",
    },
  },
);

function TabsList({
  className,
  variant = "default",
  layout = "default",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      data-layout={layout}
      className={cn(tabsListVariants({ variant, layout }), className)}
      {...props}
    />
  );
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring text-foreground/60 hover:text-foreground dark:text-muted-foreground dark:hover:text-foreground border-border relative inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-md rounded-t-lg rounded-b-none border border-b-0 px-4 py-0.5 text-sm font-medium whitespace-nowrap transition-transform duration-200 not-last:-mr-px group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start group-data-[layout=compact]/tabs-list:w-fit group-data-[layout=compact]/tabs-list:flex-none focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        "group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:data-active:bg-transparent dark:group-data-[variant=line]/tabs-list:data-active:border-transparent dark:group-data-[variant=line]/tabs-list:data-active:bg-transparent",
        "data-active:bg-background dark:data-active:text-foreground dark:data-active:border-input dark:data-active:bg-input/30 data-active:text-foreground data-[state=active]:bg-card data-[state=active]:z-10 data-[state=active]:-mb-px data-[state=active]:h-12 data-[state=active]:font-medium",
        "after:bg-foreground after:absolute after:opacity-0 after:transition-opacity group-data-horizontal/tabs:after:inset-x-0 group-data-horizontal/tabs:after:-bottom-1.25 group-data-horizontal/tabs:after:h-0.5 group-data-vertical/tabs:after:inset-y-0 group-data-vertical/tabs:after:-right-1 group-data-vertical/tabs:after:w-0.5 group-data-[variant=line]/tabs-list:data-active:after:opacity-100",
        className,
      )}
      {...props}
    />
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn(
        "bg-card flex-1 rounded-b-lg border-r border-b border-l px-4 py-6 text-sm outline-none",
        className,
      )}
      {...props}
    />
  );
}

/** @knipignore */
export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants };
